'use client';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateShiftForm } from '@hooks/useCreateShiftForm';
import { useNavigate } from '@hooks/useNavigation';
import { useSchoolListing } from '@hooks/useSchoolListing';
import { useShifts } from '@hooks/useShifts';
import Accept from '@icons/Accept.svg';
import Check from '@icons/Check.svg';
import Class from '@icons/Class.svg';
import Date from '@icons/Date.svg';
import Decline from '@icons/Decline.svg';
import Email from '@icons/Email.svg';
import Time from '@icons/Time.svg';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { ROLES } from 'types';

import { Button } from '@components/Button';
import { BottomContent, PageBottom, PageContent, PageGrid } from '@components/Common';
import { DateInput } from '@components/DateInput';
import { Input } from '@components/Input';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';
import { PhoneInput } from '@components/PhoneInput';
import { CheckboxWrapper, CustomCheckbox, HiddenCheckbox, Text } from '@components/TermsCheckbox';
import { TimeInput } from '@components/TimeInput';
import { showToast } from '@components/ToastContainer';

import { FORM_STEPS, type ShiftFormData } from '@utils/shiftConstants';

import {
  getBottomButtonLabel,
  getShiftsPath,
  getStepDescription,
  getStepTitle,
  prepareShiftData,
  processInputValue,
  shouldNavigateBack,
  shouldNavigateToNextStep,
  shouldShowBackButton,
} from './helpers';
import {
  ApprovalItem,
  ApprovalList,
  ApprovalText,
  ButtonsContainer,
  CheckIconWrapper,
  CreateShiftFormWrapper,
  ShiftDescription,
  ShiftTitle,
  TimeContainer,
} from './styles';

/**
 * CreateShift Component
 *
 * Component that allows schools to create new shift assignments.
 * Features a two-step wizard interface for collecting shift details and contact information.

 */
export const CreateShift: React.FC = () => {
  const { t } = useTranslation('common');
  const { goToPath } = useNavigate();
  const { createShift, creating, loading, error } = useShifts();
  const { listing } = useSchoolListing();
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);

  const {
    currentStep,
    formData,
    errors,
    errorFields,
    updateField,
    validateCurrentStep,
    nextStep,
    previousStep,
    getValidationToastMessage,
  } = useCreateShiftForm();

  // Memoized event handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      const processedValue = processInputValue(type, value, checked);

      updateField(name as keyof ShiftFormData, processedValue);
    },
    [updateField],
  );

  const handleWhatsAppToggle = useCallback(
    (checked: boolean) => {
      updateField('school_enabled_whatsapp', checked);
    },
    [updateField],
  );

  // Handle back navigation - memoized to prevent useEffect from running unnecessarily
  const handleBack = useCallback(() => {
    if (shouldNavigateBack(currentStep)) {
      previousStep();
    } else {
      // Go back to shifts page instead of new-shift to avoid redirect loop
      goToPath(getShiftsPath(ROLES.SCHOOL));
    }
  }, [currentStep, previousStep, goToPath]);

  // Set up header back button
  useEffect(() => {
    // Hide back button on APROVE step
    const showBackButton = shouldShowBackButton(currentStep);
    setShowBackButton(showBackButton);
    setOnBack(handleBack);

    // Clean up on unmount
    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [setShowBackButton, setOnBack, handleBack, currentStep]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateCurrentStep()) {
        const { title, message } = getValidationToastMessage(errorFields, formData);
        showToast('error', title, message);
        return;
      }

      // Navigate immediately for smooth redirection
      goToPath(getShiftsPath(ROLES.SCHOOL));

      // Create shift in background
      try {
        const shiftData = prepareShiftData(formData, listing?.id?.toString());
        await createShift(shiftData);
      } catch (error) {
        console.error('Failed to create shift:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to create shift';
        showToast('error', t('toast.error'), errorMessage);
      }
    },
    [
      validateCurrentStep,
      createShift,
      formData,
      listing?.id,
      goToPath,
      getValidationToastMessage,
      errorFields,
      t,
    ],
  );

  const handleBottomButtonClick = useCallback(() => {
    if (shouldNavigateToNextStep(currentStep)) {
      nextStep();
    } else {
      handleSubmit(new Event('submit') as any);
    }
  }, [currentStep, nextStep, handleSubmit]);

  const handleCancel = useCallback(() => {
    goToPath(getShiftsPath(ROLES.SCHOOL));
  }, [goToPath]);

  // Memoized form sections
  const detailsSection = useMemo(
    () => (
      <>
        <Input
          type="text"
          id="text"
          placeholder={t('schoolShifts.addGroup')}
          name="group"
          label={t('schoolShifts.group')}
          value={formData.group}
          hasError={!!errors.group}
          onChange={handleInputChange}
          icon={<Class />}
          hasIcon={true}
          required
        />

        <DateInput
          id="date"
          placeholder={t('schoolShifts.addDate')}
          name="shift_date"
          label={t('schoolShifts.date')}
          value={formData.shift_date}
          hasError={!!errors.shift_date}
          icon={<Date />}
          onChange={handleInputChange}
          required
        />
        <TimeContainer>
          <TimeInput
            id="time_from"
            name="time_from"
            label={t('schoolShifts.from')}
            placeholder="00:00"
            value={formData.time_from}
            hasError={!!errors.time_from}
            hasIcon={true}
            icon={<Time />}
            onChange={handleInputChange}
            required
          />
          <TimeInput
            id="time_till"
            name="time_till"
            label={t('schoolShifts.till')}
            placeholder="00:00"
            value={formData.time_till}
            hasError={!!errors.time_till}
            hasIcon={true}
            icon={<Time />}
            onChange={handleInputChange}
            required
          />
        </TimeContainer>
      </>
    ),
    [formData, errors, handleInputChange, t],
  );

  const contactsSection = useMemo(
    () => (
      <>
        <Input
          type="email"
          id="contact_email"
          placeholder={t('enterEmail')}
          name="contact_email"
          label={t('email')}
          value={formData.contact_email}
          hasError={!!errors.contact_email}
          hasIcon={true}
          icon={<Email />}
          onChange={handleInputChange}
        />
        <PhoneInput
          id="contact_phone"
          placeholder={t('enterPhone')}
          name="contact_phone"
          label={t('phoneNumber')}
          value={formData.contact_phone}
          hasError={!!errors.contact_phone}
          onChange={handleInputChange}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
          <CheckboxWrapper>
            <HiddenCheckbox
              type="checkbox"
              id="whatsapp"
              checked={formData.school_enabled_whatsapp}
              onChange={() => handleWhatsAppToggle(!formData.school_enabled_whatsapp)}
              name="school_enabled_whatsapp"
            />
            <CustomCheckbox />
          </CheckboxWrapper>
          <Text htmlFor="whatsapp">{t('schoolShifts.whatsappCheck')}</Text>
        </div>
      </>
    ),
    [formData, errors, handleInputChange, handleWhatsAppToggle, t],
  );

  const approvalSection = useMemo(
    () => (
      <ApprovalList>
        <ApprovalItem>
          <CheckIconWrapper>
            <Check />
          </CheckIconWrapper>
          <ApprovalText>{t('schoolShifts.publishShiftDescriptionStep1')}</ApprovalText>
        </ApprovalItem>
        <ApprovalItem>
          <CheckIconWrapper>
            <Check />
          </CheckIconWrapper>
          <ApprovalText>{t('schoolShifts.publishShiftDescriptionStep2')}</ApprovalText>
        </ApprovalItem>
        <ApprovalItem>
          <CheckIconWrapper>
            <Check />
          </CheckIconWrapper>
          <ApprovalText>{t('schoolShifts.publishShiftDescriptionStep3')}</ApprovalText>
        </ApprovalItem>
      </ApprovalList>
    ),
    [t],
  );

  const getCurrentSection = useCallback(() => {
    if (currentStep === FORM_STEPS.DETAILS) return detailsSection;
    if (currentStep === FORM_STEPS.CONTACTS) return contactsSection;
    return approvalSection;
  }, [currentStep, detailsSection, contactsSection, approvalSection]);

  return (
    <PageGrid className="full-height">
      <PageContent>
        <LoadingErrorHandler isLoading={loading} isError={!!error}>
          <ShiftTitle>{getStepTitle(currentStep, t)}</ShiftTitle>
          {getStepDescription(currentStep, t) && (
            <ShiftDescription>{getStepDescription(currentStep, t)}</ShiftDescription>
          )}
          <CreateShiftFormWrapper onSubmit={handleSubmit} autoComplete="off">
            {getCurrentSection()}
          </CreateShiftFormWrapper>
        </LoadingErrorHandler>
      </PageContent>
      <PageBottom>
        <BottomContent>
          {currentStep === FORM_STEPS.APROVE ? (
            <ButtonsContainer>
              <Button
                label={t('modals.cancel')}
                type="button"
                onClick={handleCancel}
                variant="secondary"
                disabled={creating}
                icon={<Decline />}
              />
              <Button
                label={getBottomButtonLabel(currentStep, t)}
                type="button"
                onClick={handleBottomButtonClick}
                variant="primary"
                disabled={creating}
                icon={<Accept />}
              />
            </ButtonsContainer>
          ) : (
            <Button
              label={getBottomButtonLabel(currentStep, t)}
              type="button"
              onClick={handleBottomButtonClick}
              variant="primary"
              disabled={creating}
            />
          )}
        </BottomContent>
      </PageBottom>
    </PageGrid>
  );
};
